// src/pages/DocumentManagementSystem/utils/dmsRichTextPdf.js
//
// Go-live parity (2026-09-24, audit DMS#7): the legacy DevExpress report
// printed the full rich text of every section — pictures, tables, bold /
// italic / underline, lists. This module lays a section's HTML out with
// jsPDF itself (no html2canvas, no rasterising), so text stays selectable
// and small:
//   • paragraphs / headings with bold, italic, underline, strike, colour,
//     highlight and links (word-wrapped, left / centre / right aligned);
//   • bullet and numbered lists (Quill's flat ql-indent-N lists and nested
//     <ul>/<ol> from legacy RTF);
//   • pictures (Quill inline base64 screenshots and RtfPipe data-URI
//     pictures) scaled to the page width, re-encoded as JPEG at ~150 dpi,
//     never cut across a page;
//   • tables (the dmsTable blot and RtfPipe tables) as real grids through
//     jspdf-autotable — header rows repeat, rows are not split across pages
//     where avoidable, pictures inside cells are drawn in the cell.
// Section HTML comes from GET /api/dms/{id}/pdf-data (DmsPdfDataBuilder):
// dashboard-authored Quill HTML as stored, or legacy RTF converted to HTML
// by RtfPipe on the server. Pictures a browser cannot decode (legacy WMF /
// EMF metafiles) print a short placeholder instead of breaking the PDF.

const IMG_DPI = 150;            // printed resolution of re-encoded pictures
const JPEG_QUALITY = 0.82;
const HEADING_SIZES = { H1: 14, H2: 12.5, H3: 11, H4: 10, H5: 9.5, H6: 9.5 };
const INDENT_STEP = 18;         // pt per Quill ql-indent level / list depth
const BLOCK_TAGS = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE",
    "PRE", "SECTION", "ARTICLE", "CENTER", "ADDRESS", "DL", "DT", "DD", "FIGURE",
    "FIGCAPTION", "HEADER", "FOOTER", "MAIN", "NAV", "ASIDE", "FORM", "FIELDSET"]);
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "HEAD", "TITLE", "META", "LINK", "NOSCRIPT",
    "TEMPLATE", "IFRAME", "OBJECT", "EMBED", "SVG", "BUTTON", "INPUT", "SELECT", "TEXTAREA"]);

// ── Small CSS helpers ────────────────────────────────────────────────
const styleOf = (el, prop) => {
    try { return (el.style && el.style.getPropertyValue(prop)) || ""; } catch { return ""; }
};

/** CSS length → pt (px, pt, in, cm, mm, em); `ref` resolves %. NaN when unknown. */
const toPt = (value, ref = NaN, emSize = 9) => {
    const m = String(value || "").trim().match(/^(-?[\d.]+)\s*(px|pt|in|cm|mm|em|rem|%)?$/i);
    if (!m) return NaN;
    const n = parseFloat(m[1]);
    switch ((m[2] || "px").toLowerCase()) {
        case "pt": return n;
        case "in": return n * 72;
        case "cm": return n * 72 / 2.54;
        case "mm": return n * 72 / 25.4;
        case "em": case "rem": return n * emSize;
        case "%": return n / 100 * ref;
        default: return n * 0.75;   // px (96 dpi)
    }
};

const NAMED_COLORS = {
    black: [0, 0, 0], white: [255, 255, 255], red: [255, 0, 0], green: [0, 128, 0],
    blue: [0, 0, 255], yellow: [255, 255, 0], gray: [128, 128, 128], grey: [128, 128, 128],
    orange: [255, 165, 0], purple: [128, 0, 128], navy: [0, 0, 128], maroon: [128, 0, 0],
    teal: [0, 128, 128], silver: [192, 192, 192], lime: [0, 255, 0], aqua: [0, 255, 255],
    cyan: [0, 255, 255], fuchsia: [255, 0, 255], magenta: [255, 0, 255], olive: [128, 128, 0],
};
/** CSS colour → [r,g,b] or null (transparent / unknown). */
const parseColor = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v || v === "transparent" || v === "inherit" || v === "initial" || v === "auto") return null;
    if (NAMED_COLORS[v]) return NAMED_COLORS[v];
    let m = v.match(/^#([0-9a-f]{3})$/);
    if (m) return m[1].split("").map((h) => parseInt(h + h, 16));
    m = v.match(/^#([0-9a-f]{6})/);
    if (m) return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
    m = v.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/);
    if (m) {
        if (m[4] !== undefined && parseFloat(m[4]) === 0) return null;
        return [m[1], m[2], m[3]].map((x) => Math.max(0, Math.min(255, Math.round(parseFloat(x)))));
    }
    return null;
};
const luminance = (c) => (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;

const alignOf = (el, fallback) => {
    const cls = el.className && typeof el.className === "string" ? el.className : "";
    const m = cls.match(/ql-align-(center|right|justify)/);
    if (m) return m[1];
    const ta = (styleOf(el, "text-align") || el.getAttribute("align") || "").toLowerCase();
    if (ta === "center" || ta === "right" || ta === "justify" || ta === "left") return ta;
    return el.tagName === "CENTER" ? "center" : fallback;
};
const qlIndent = (el) => {
    const cls = el.className && typeof el.className === "string" ? el.className : "";
    const m = cls.match(/ql-indent-(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
};

// ── Inline style of a run ────────────────────────────────────────────
const inlineStyle = (el, st, baseSize) => {
    const tag = el.tagName;
    const s = { ...st };
    if (tag === "B" || tag === "STRONG") s.bold = true;
    if (tag === "I" || tag === "EM" || tag === "CITE" || tag === "DFN") s.italic = true;
    if (tag === "U" || tag === "INS") s.underline = true;
    if (tag === "S" || tag === "STRIKE" || tag === "DEL") s.strike = true;
    if (tag === "SUB" || tag === "SUP" || tag === "SMALL") s.size = Math.max(6, s.size * 0.8);
    if (tag === "A") {
        const href = el.getAttribute("href") || "";
        if (/^(https?:|mailto:)/i.test(href)) { s.link = href; s.color = [5, 99, 193]; s.underline = true; }
    }
    if (tag === "FONT") {
        const c = parseColor(el.getAttribute("color"));
        if (c) s.color = c;
    }
    const fw = styleOf(el, "font-weight").toLowerCase();
    if (fw === "bold" || fw === "bolder" || parseInt(fw, 10) >= 600) s.bold = true;
    else if (fw === "normal" || (parseInt(fw, 10) > 0 && parseInt(fw, 10) < 600)) s.bold = false;
    const fs = styleOf(el, "font-style").toLowerCase();
    if (fs === "italic" || fs === "oblique") s.italic = true;
    else if (fs === "normal") s.italic = false;
    const td = (styleOf(el, "text-decoration") || styleOf(el, "text-decoration-line")).toLowerCase();
    if (td.includes("underline")) s.underline = true;
    if (td.includes("line-through")) s.strike = true;
    const color = parseColor(styleOf(el, "color"));
    if (color) s.color = color;
    const bg = parseColor(styleOf(el, "background-color") || styleOf(el, "background"));
    if (bg && luminance(bg) < 0.995) s.bg = bg;
    const size = toPt(styleOf(el, "font-size"), NaN, s.size);
    // Legacy RTF sizes (10–12 pt) are scaled to the PDF body size (11 pt → base).
    if (size > 0) s.size = Math.max(6.5, Math.min(16, baseSize * size / 11));
    return s;
};

// ── HTML → block list ────────────────────────────────────────────────
/**
 * Blocks: { type: "para", runs, align, indent, marker, spaceBefore, spaceAfter }
 *         { type: "image", el, align, indent }
 *         { type: "table", el, indent }
 *         { type: "hr" }
 * A run is { text, style } or { br: true }.
 */
export const htmlToBlocks = (root, baseSize = 9) => {
    const blocks = [];
    let cur = null;
    const newPara = (bc) => {
        let marker = null;
        if (bc.li && !bc.li.used) { marker = bc.li.marker; bc.li.used = true; }
        cur = {
            type: "para", runs: [], align: bc.align || "left", indent: bc.indent || 0, marker,
            pre: !!bc.pre, spaceBefore: bc.heading ? 5 : 0, spaceAfter: bc.heading ? 3 : 1.5,
        };
    };
    const flush = () => {
        if (!cur) return;
        const hasText = cur.runs.some((r) => r.br || (r.text && r.text.trim() !== ""));
        if (hasText || cur.marker) blocks.push(cur);
        cur = null;
    };
    const walk = (node, st, bc) => {
        if (node.nodeType === 3) {
            let text = node.nodeValue || "";
            // Collapse HTML whitespace — but keep non-breaking spaces (legacy
            // RTF indents with them).
            if (!bc.pre) text = text.replace(/[ \t\n\r\f]+/g, " ");
            if (!text) return;
            if (!cur) {
                if (text.trim() === "" && !bc.pre) return;
                newPara(bc);
            }
            if (bc.pre && text.includes("\n")) {
                text.split("\n").forEach((part, i) => {
                    if (i > 0) cur.runs.push({ br: true });
                    if (part) cur.runs.push({ text: part, style: st });
                });
            } else cur.runs.push({ text, style: st });
            return;
        }
        if (node.nodeType !== 1) return;
        const tag = node.tagName;
        if (SKIP_TAGS.has(tag)) return;
        if (tag === "BR") {
            if (!cur) newPara(bc);
            cur.runs.push({ br: true });
            return;
        }
        if (tag === "IMG") {
            flush();
            blocks.push({ type: "image", el: node, align: bc.align || "left", indent: bc.indent || 0 });
            return;
        }
        if (tag === "TABLE") {
            flush();
            blocks.push({ type: "table", el: node, indent: bc.indent || 0 });
            return;
        }
        if (tag === "HR") {
            flush();
            blocks.push({ type: "hr" });
            return;
        }
        if (tag === "UL" || tag === "OL") {
            flush();
            const list = {
                ordered: tag === "OL", depth: (bc.listDepth || 0) + 1, counters: [],
                // A nested list starts under its parent item's text.
                indent: bc.inLi ? (bc.indentBase || 0) : (bc.indent || 0),
            };
            const start = parseInt(node.getAttribute("start"), 10);
            if (start > 1) list.start = start;
            const inner = { ...bc, list, listDepth: list.depth, li: null };
            node.childNodes.forEach((c) => walk(c, st, inner));
            flush();
            return;
        }
        if (tag === "LI") {
            flush();
            const list = bc.list || { ordered: false, depth: 1, counters: [], indent: bc.indent || 0 };
            const level = qlIndent(node);
            // Quill writes nested / ordered items as <li data-list="bullet|ordered">.
            const dataList = node.getAttribute("data-list");
            const ordered = dataList ? dataList === "ordered" : list.ordered;
            list.counters[level] = (list.counters[level] || (level === 0 && list.start ? list.start - 1 : 0)) + 1;
            list.counters.length = level + 1;   // a new item resets deeper numbering
            const bullets = ["•", "-", "•"];
            const marker = ordered ? `${list.counters[level]}.` : bullets[(list.depth - 1 + level) % 3];
            const indent = list.indent + level * INDENT_STEP;
            const inner = {
                ...bc, align: alignOf(node, "left"), indent, li: { marker, used: false },
                indentBase: indent + INDENT_STEP, inLi: true,
            };
            node.childNodes.forEach((c) => walk(c, st, inner));
            if (!inner.li.used) { newPara(inner); }
            flush();
            return;
        }
        if (BLOCK_TAGS.has(tag)) {
            flush();
            const heading = HEADING_SIZES[tag];
            const ml = toPt(styleOf(node, "margin-left") || styleOf(node, "padding-left"), NaN, st.size);
            const extra = (qlIndent(node) * INDENT_STEP) + (ml > 0 ? Math.min(ml, 200) : 0)
                + (tag === "BLOCKQUOTE" || tag === "DD" ? INDENT_STEP : 0);
            const inner = {
                ...bc, align: alignOf(node, bc.align || "left"), heading: heading || bc.heading,
                indent: (bc.indent || 0) + extra, indentBase: (bc.indentBase || 0) + extra,
                pre: bc.pre || tag === "PRE",
            };
            let s2 = inlineStyle(node, st, baseSize);
            if (heading) s2 = { ...s2, bold: true, size: heading };
            node.childNodes.forEach((c) => walk(c, s2, inner));
            flush();
            return;
        }
        const s2 = inlineStyle(node, st, baseSize);
        node.childNodes.forEach((c) => walk(c, s2, bc));
    };
    const base = { size: baseSize, bold: false, italic: false, underline: false, strike: false, color: null, bg: null, link: null };
    root.childNodes.forEach((c) => walk(c, base, { align: "left", indent: 0 }));
    flush();
    return blocks;
};

// ── Pictures ─────────────────────────────────────────────────────────
const loadImage = (src) => new Promise((resolve) => {
    if (typeof Image === "undefined") { resolve(null); return; }
    const img = new Image();
    let done = false;
    const finish = (ok) => { if (!done) { done = true; resolve(ok && img.naturalWidth > 0 ? img : null); } };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    setTimeout(() => finish(img.complete), 15000);   // never hang the PDF on one picture
    img.src = src;
});

/** Decode a picture once per document (cache keyed by src). null = not printable. */
const decodePicture = async (src, cache) => {
    if (cache.decoded.has(src)) return cache.decoded.get(src);
    let result = null;
    // Browsers cannot draw WMF / EMF / PICT metafiles (legacy RTF) — skip them.
    const printable = /^data:image\/(png|jpe?g|gif|bmp|webp|svg\+xml)[;,]/i.test(src)
        || /^(https?:)?\/\//i.test(src) || /^\/(?!\/)/.test(src);
    if (printable) result = await loadImage(src);
    cache.decoded.set(src, result);
    return result;
};

/**
 * Re-encode a picture as a JPEG at IMG_DPI for its printed size (white
 * background for transparent PNGs). Returns { dataUrl, alias } or null.
 */
const encodePicture = (img, src, wPt, hPt, cache) => {
    const pxW = Math.max(1, Math.min(img.naturalWidth, Math.round(wPt / 72 * IMG_DPI)));
    const pxH = Math.max(1, Math.round(pxW * img.naturalHeight / img.naturalWidth));
    const key = `${pxW}x${pxH}|${src.length}|${src.slice(-64)}|${src.slice(0, 96)}`;
    if (cache.encoded.has(key)) return cache.encoded.get(key);
    let out = null;
    try {
        const canvas = document.createElement("canvas");
        canvas.width = pxW;
        canvas.height = pxH;
        const g = canvas.getContext("2d");
        g.fillStyle = "#ffffff";
        g.fillRect(0, 0, pxW, pxH);
        g.imageSmoothingEnabled = true;
        g.imageSmoothingQuality = "high";
        g.drawImage(img, 0, 0, pxW, pxH);
        out = { dataUrl: canvas.toDataURL("image/jpeg", JPEG_QUALITY), alias: `dms-pic-${cache.encoded.size + 1}` };
        canvas.width = canvas.height = 0;   // release the bitmap early
    } catch {
        out = null;   // tainted canvas (cross-origin URL) or out of memory
    }
    cache.encoded.set(key, out);
    return out;
};

/** Printed size (pt) of an <img>: its width / height attributes or CSS, else natural size at 96 dpi. */
const pictureSize = (el, img, maxW, maxH) => {
    const ratio = img.naturalHeight / img.naturalWidth;
    let w = toPt(styleOf(el, "width"), maxW);
    let h = toPt(styleOf(el, "height"), NaN);
    if (!(w > 0)) w = toPt(el.getAttribute("width"), maxW);
    if (!(h > 0)) h = toPt(el.getAttribute("height"), NaN);
    if (w > 0 && !(h > 0)) h = w * ratio;
    else if (h > 0 && !(w > 0)) w = h / ratio;
    else if (!(w > 0) || !(h > 0)) { w = img.naturalWidth * 0.75; h = img.naturalHeight * 0.75; }
    const scale = Math.min(1, maxW / w, maxH / h);
    return { w: w * scale, h: h * scale };
};

// Let the browser paint between heavy steps so a long document never
// freezes the tab.
const yieldToBrowser = () => new Promise((r) => setTimeout(r, 0));

// ── Renderer ─────────────────────────────────────────────────────────
/**
 * Lay one section's HTML out at the cursor. `ctx`:
 *   doc, autoTable, x (left margin), width (content width), pos ({ y } getter/setter),
 *   ensureSpace(h), top (page top y), usableH (content height of a page),
 *   ink (grey level), muted, pdfSafe, tableBase (shared autotable options),
 *   baseSize, cache (createPictureCache()).
 * Returns false when there was nothing to lay out.
 */
export const renderRichHtml = async (html, ctx) => {
    if (!html || typeof DOMParser === "undefined") return false;
    const { doc, pdfSafe } = ctx;
    const baseSize = ctx.baseSize || 9;
    const root = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html").body.firstChild;
    if (!root) return false;
    const blocks = htmlToBlocks(root, baseSize);
    if (!blocks.length) return false;

    const fontStyle = (s) => (s.bold && s.italic ? "bolditalic" : s.bold ? "bold" : s.italic ? "italic" : "normal");
    const setRunFont = (s) => doc.setFont("helvetica", fontStyle(s)).setFontSize(s.size);
    const colorOf = (s) => (s.color && luminance(s.color) < 0.93 ? s.color : [ctx.ink, ctx.ink, ctx.ink]);

    // ── Paragraph: word-wrap styled runs ─────────────────────────────
    const drawPara = (b) => {
        const markerSize = (b.runs.find((r) => !r.br) || {}).style?.size || baseSize;
        let markerW = 0;
        if (b.marker) {
            doc.setFont("helvetica", "normal").setFontSize(markerSize);
            markerW = Math.max(12, doc.getTextWidth(b.marker) + 5);
        }
        const left = ctx.x + Math.min(b.indent, ctx.width * 0.5);
        const textX = left + markerW;
        const avail = Math.max(40, ctx.x + ctx.width - textX);

        // Tokens: words and spaces with their style; "br" forces a line.
        const tokens = [];
        b.runs.forEach((r) => {
            if (r.br) { tokens.push({ br: true }); return; }
            const text = pdfSafe(r.text);
            text.split(/(\s+)/).forEach((piece) => {
                if (!piece) return;
                tokens.push({ text: piece.replace(/\s/g, " "), space: /^\s+$/.test(piece), style: r.style });
            });
        });
        const measure = (t) => { setRunFont(t.style); return doc.getTextWidth(t.text); };

        const lines = [];
        let line = { items: [], w: 0 };
        const pushLine = () => {
            while (line.items.length && line.items[line.items.length - 1].space) {
                line.w -= line.items.pop().w;
            }
            lines.push(line);
            line = { items: [], w: 0 };
        };
        tokens.forEach((t) => {
            if (t.br) { pushLine(); return; }
            if (t.space && line.items.length === 0 && !b.pre) return;   // no leading space on a wrapped line
            let w = measure(t);
            if (line.w + w <= avail || (t.space && !b.pre)) {
                line.items.push({ ...t, w });
                line.w += w;
                return;
            }
            if (line.items.length) pushLine();
            if (t.space) return;
            // A word longer than the whole line is broken by characters.
            let rest = t.text;
            while (rest && measure({ ...t, text: rest }) > avail) {
                let n = rest.length - 1;
                while (n > 1 && measure({ ...t, text: rest.slice(0, n) }) > avail) n--;
                const part = rest.slice(0, n);
                line.items.push({ ...t, text: part, w: measure({ ...t, text: part }) });
                line.w = line.items[0].w;
                pushLine();
                rest = rest.slice(n);
            }
            if (rest) {
                w = measure({ ...t, text: rest });
                line.items.push({ ...t, text: rest, w });
                line.w += w;
            }
        });
        if (line.items.length || !lines.length) pushLine();

        ctx.pos.y += b.spaceBefore || 0;
        lines.forEach((ln, li) => {
            const maxSize = ln.items.reduce((m, it) => Math.max(m, it.style.size), ln.items.length ? 0 : markerSize) || baseSize;
            const lineH = maxSize * 1.28;
            ctx.ensureSpace(lineH);
            const baseline = ctx.pos.y + maxSize * 0.95;
            if (li === 0 && b.marker) {
                doc.setFont("helvetica", "normal").setFontSize(markerSize).setTextColor(ctx.ink);
                doc.text(b.marker, left, baseline);
            }
            let x = textX;
            if (b.align === "center") x = textX + Math.max(0, (avail - ln.w) / 2);
            else if (b.align === "right") x = textX + Math.max(0, avail - ln.w);
            // Merge neighbouring tokens of the same style into one text call
            // (cleaner, searchable text in the PDF).
            const segs = [];
            ln.items.forEach((it) => {
                const last = segs[segs.length - 1];
                if (last && last.style === it.style) { last.text += it.text; last.w += it.w; }
                else segs.push({ text: it.text, w: it.w, style: it.style });
            });
            segs.forEach((sg) => {
                const s = sg.style;
                const c = colorOf(s);
                if (s.bg) {
                    doc.setFillColor(...s.bg);
                    doc.rect(x, baseline - s.size * 0.82, sg.w, s.size * 1.08, "F");
                }
                setRunFont(s);
                doc.setTextColor(...c);
                if (sg.text.trim() !== "") doc.text(sg.text, x, baseline);
                if (s.underline || s.strike) {
                    doc.setDrawColor(...c).setLineWidth(Math.max(0.4, s.size / 18));
                    if (s.underline) doc.line(x, baseline + 1.2, x + sg.w, baseline + 1.2);
                    if (s.strike) doc.line(x, baseline - s.size * 0.3, x + sg.w, baseline - s.size * 0.3);
                }
                if (s.link) {
                    try { doc.link(x, baseline - s.size * 0.85, sg.w, s.size * 1.1, { url: s.link }); } catch { /* link is decoration only */ }
                }
                x += sg.w;
            });
            ctx.pos.y += lineH;
        });
        ctx.pos.y += b.spaceAfter || 0;
    };

    // ── Placeholder for a picture the browser cannot draw ────────────
    const drawPlaceholder = (indent) => {
        ctx.ensureSpace(14);
        doc.setFont("helvetica", "italic").setFontSize(8).setTextColor(ctx.muted);
        doc.text("[Picture not printable here - open the document in the DMS to view it]", ctx.x + indent, ctx.pos.y + 9);
        ctx.pos.y += 14;
    };

    // ── Picture block ────────────────────────────────────────────────
    const drawImage = async (b) => {
        const src = b.el.getAttribute("src") || "";
        const img = src ? await decodePicture(src, ctx.cache) : null;
        if (!img) { if (src) drawPlaceholder(b.indent); return; }
        const indent = Math.min(b.indent, ctx.width * 0.5);
        const maxW = ctx.width - indent;
        const { w, h } = pictureSize(b.el, img, maxW, ctx.usableH - 8);
        const pic = encodePicture(img, src, w, h, ctx.cache);
        if (!pic) { drawPlaceholder(b.indent); return; }
        ctx.ensureSpace(h + 4);
        let x = ctx.x + indent;
        if (b.align === "center") x = ctx.x + indent + (maxW - w) / 2;
        else if (b.align === "right") x = ctx.x + ctx.width - w;
        doc.addImage(pic.dataUrl, "JPEG", x, ctx.pos.y + 2, w, h, pic.alias, "NONE");
        ctx.pos.y += h + 6;
        await yieldToBrowser();
    };

    // ── Table block ──────────────────────────────────────────────────
    const drawTable = async (b) => {
        const el = b.el;
        const trs = Array.from(el.rows || []).filter((tr) => {
            // Rows of nested tables belong to their own table.
            let p = tr.parentElement;
            while (p && p.tagName !== "TABLE") p = p.parentElement;
            return p === el;
        });
        if (!trs.length) return;
        const indent = Math.min(b.indent, ctx.width * 0.4);
        const tableW = ctx.width - indent;

        // Column count with row spans taken into account.
        const occupied = [];
        let nCols = 0;
        const rowsCells = trs.map((tr, ri) => {
            const cells = Array.from(tr.cells);
            let col = 0;
            const out = cells.map((td) => {
                while (occupied[ri] && occupied[ri][col]) col++;
                const colSpan = Math.max(1, parseInt(td.getAttribute("colspan"), 10) || 1);
                const rowSpan = Math.max(1, Math.min(trs.length - ri, parseInt(td.getAttribute("rowspan"), 10) || 1));
                for (let r = ri; r < ri + rowSpan; r++) {
                    occupied[r] = occupied[r] || [];
                    for (let c = col; c < col + colSpan; c++) occupied[r][c] = true;
                }
                const cell = { td, col, colSpan, rowSpan };
                col += colSpan;
                return cell;
            });
            nCols = Math.max(nCols, (occupied[ri] || []).length, col);
            return out;
        });
        if (!nCols) return;

        // Column widths: the table's own (legacy RTF tables carry them),
        // else equal when pictures sit in cells, else automatic.
        let colW = null;
        const widthRow = rowsCells.find((r) => r.length === nCols && r.every((c) => c.colSpan === 1));
        if (widthRow) {
            const ws = widthRow.map((c) => toPt(styleOf(c.td, "width") || c.td.getAttribute("width"), tableW));
            if (ws.every((v) => v > 0)) {
                const sum = ws.reduce((a, v) => a + v, 0);
                const scale = Math.min(1, tableW / sum);
                colW = ws.map((v) => v * scale);
            }
        }
        const hasPictures = !!el.querySelector("img");
        if (!colW && hasPictures) colW = Array(nCols).fill(tableW / nCols);

        const fontSize = ctx.tableBase.styles.fontSize || 8.5;
        const pad = typeof ctx.tableBase.styles.cellPadding === "number" ? ctx.tableBase.styles.cellPadding : 4;
        const lhf = typeof doc.getLineHeightFactor === "function" ? doc.getLineHeightFactor() : 1.15;

        const toCell = async (c, header) => {
            const td = c.td;
            // Cell text keeps its line breaks, bullets and numbering.
            const clone = td.cloneNode(true);
            clone.querySelectorAll("table").forEach((t) => t.replaceWith(...Array.from(t.rows).map((r) => {
                const p = td.ownerDocument.createElement("p");
                p.textContent = Array.from(r.cells).map((x) => x.textContent.trim()).join("  |  ");
                return p;
            })));
            const text = pdfSafe(ctx.htmlToPlainText(clone.innerHTML));
            const styles = {};
            // Whole-cell bold only (autotable cannot mix fonts inside a cell).
            const squash = (t) => String(t || "").replace(/\s+/g, "");
            const txt = squash(td.textContent);
            const boldText = squash(Array.from(td.querySelectorAll("b, strong")).map((x) => x.textContent).join(""));
            if (header || td.tagName === "TH" || (txt && boldText.length >= txt.length)) styles.fontStyle = "bold";
            const al = alignOf(td, "");
            if (al === "center" || al === "right") styles.halign = al;
            const va = (styleOf(td, "vertical-align") || td.getAttribute("valign") || "").toLowerCase();
            if (va === "middle" || va === "bottom") styles.valign = va;
            const bg = parseColor(styleOf(td, "background-color") || styleOf(td, "background") || td.getAttribute("bgcolor"));
            if (bg && luminance(bg) < 0.99) {
                styles.fillColor = bg;
                if (luminance(bg) < 0.5) styles.textColor = 255;
            }
            // Pictures in the cell: decoded now, drawn in didDrawCell under the text.
            const pics = [];
            const imgs = Array.from(td.querySelectorAll("img"));
            if (imgs.length) {
                const span = colW ? colW.slice(c.col, c.col + c.colSpan).reduce((a, v) => a + v, 0) : tableW / nCols;
                const innerW = Math.max(20, span - pad * 2);
                for (const im of imgs) {
                    const src = im.getAttribute("src") || "";
                    const decoded = src ? await decodePicture(src, ctx.cache) : null;
                    if (!decoded) continue;
                    const size = pictureSize(im, decoded, innerW, Math.min(320, ctx.usableH - 60));
                    const enc = encodePicture(decoded, src, size.w, size.h, ctx.cache);
                    if (enc) pics.push({ ...enc, w: size.w, h: size.h });
                }
                if (pics.length) {
                    doc.setFont("helvetica", styles.fontStyle || "normal").setFontSize(fontSize);
                    const textLines = text ? doc.splitTextToSize(text, innerW).length : 0;
                    const picsH = pics.reduce((a, p) => a + p.h + 3, 0);
                    styles.minCellHeight = textLines * fontSize * lhf + picsH + pad * 2 + 2;
                }
            }
            return { content: text, colSpan: c.colSpan, rowSpan: c.rowSpan, styles, _pics: pics };
        };

        // Leading header rows (thead or all-<th>) repeat on every page.
        const head = [];
        const body = [];
        let inHead = true;
        for (let ri = 0; ri < trs.length; ri++) {
            const tr = trs[ri];
            const cells = rowsCells[ri];
            const isHead = inHead && cells.length > 0
                && ((tr.parentElement && tr.parentElement.tagName === "THEAD") || cells.every((c) => c.td.tagName === "TH"))
                && ri < trs.length - 1;
            if (!isHead) inHead = false;
            const row = [];
            for (const c of cells) row.push(await toCell(c, isHead));
            // Pad short rows so every row spans the full grid.
            const used = (occupied[ri] || []).filter(Boolean).length;
            for (let k = used; k < nCols; k++) row.push({ content: "" });
            (isHead ? head : body).push(row);
        }

        const columnStyles = {};
        if (colW) colW.forEach((w, i) => { columnStyles[i] = { cellWidth: w }; });
        ctx.ensureSpace(30);
        ctx.autoTable(doc, {
            ...ctx.tableBase,
            startY: ctx.pos.y + 2,
            margin: { ...ctx.tableBase.margin, left: ctx.x + indent },
            tableWidth: colW ? colW.reduce((a, v) => a + v, 0) : "auto",
            head: head.length ? head : undefined,
            body,
            columnStyles,
            rowPageBreak: "avoid",
            styles: { ...ctx.tableBase.styles },
            headStyles: { ...ctx.tableBase.headStyles, fillColor: [235, 237, 242], textColor: ctx.ink },
            didDrawCell: (data) => {
                const pics = data.cell.raw && data.cell.raw._pics;
                if (!pics || !pics.length) return;
                const textH = (data.cell.text || []).filter((t) => t !== "").length ? data.cell.text.length * fontSize * lhf : 0;
                let py = data.cell.y + data.cell.padding("top") + textH + (textH ? 3 : 0);
                pics.forEach((p) => {
                    if (py + p.h > data.cell.y + data.cell.height + 0.5) return;   // row was split — skip rather than overflow
                    doc.addImage(p.dataUrl, "JPEG", data.cell.x + data.cell.padding("left"), py, p.w, p.h, p.alias, "NONE");
                    py += p.h + 3;
                });
            },
        });
        ctx.pos.y = doc.lastAutoTable.finalY + 8;
        if (hasPictures) await yieldToBrowser();
    };

    for (const b of blocks) {
        if (b.type === "para") drawPara(b);
        else if (b.type === "image") await drawImage(b);
        else if (b.type === "table") await drawTable(b);
        else if (b.type === "hr") {
            ctx.ensureSpace(8);
            doc.setDrawColor(190).setLineWidth(0.5);
            doc.line(ctx.x, ctx.pos.y + 4, ctx.x + ctx.width, ctx.pos.y + 4);
            ctx.pos.y += 8;
        }
    }
    return true;
};

/** One cache per PDF: decoded pictures and their JPEG encodings. */
export const createPictureCache = () => ({ decoded: new Map(), encoded: new Map() });
